interface Attributes {
  [key: string]: string
}

const REGEX_LINKS = /<a(\s[^>]+)>(.*?)<\/a>/giu
const REGEX_ATTRIBUTES = /([a-z]+)=((['"])(.*?)\3)/giu
const REGEX_VIDEO_HREF = /^(?:https?:\/\/)?(?:www\.)?(?:(?:youtu(?:\.be|be\.com))|(mall\.tv))[/?](?:.+?)$/giu

export const HTML_TAGS = [
  'address',
  'article',
  'aside',
  'footer',
  'header',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hgroup',
  'main',
  'nav',
  'section',
  'blockquote',
  'dd',
  'div',
  'dl',
  'dt',
  'figcaption',
  'figure',
  'hr',
  'li',
  'main',
  'ol',
  'p',
  'pre',
  'ul',
  'a',
  'abbr',
  'b',
  'bdi',
  'bdo',
  'br',
  'cite',
  'code',
  'data',
  'dfn',
  'em',
  'i',
  'kbd',
  'mark',
  'q',
  'rb',
  'rp',
  'rt',
  'rtc',
  'ruby',
  's',
  'samp',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'time',
  'u',
  'var',
  'wbr',
  'caption',
  'col',
  'colgroup',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'img',
  'p',
  'center',
]

const parseAttributes = (input: string): Attributes => {
  const attributes: Attributes = {}
  let match = REGEX_ATTRIBUTES.exec(input)
  while (match !== null) {
    attributes[match[1]] = match[4]
    match = REGEX_ATTRIBUTES.exec(input)
  }
  return attributes
}

export const contentNormalize = (description: string, tags: string[]) => {
  description = contentRemoveCurly(description)
  description = contentReplaceLazyLoadImage(description)
  description = contentReplaceVideo(description)
  description = contentReplaceLink(description)
  description = contentRemoveHtmlComment(description)
  description = contentRemoveIf(description)
  description = contentRemoveStyleBlock(description)
  description = contentPreserveTags(description, tags)
  description = fixCommonProblems(description)
  return description
}

export const contentGlossaryTerm = (description: string) => {
  const text = description || ''

  const links = text.match('<\\s*a[^>]*>(.*?)<\\s*/\\s*a>') || []
  const glossaryLink = links.filter((aLinks) =>
    aLinks.match('link--glossary')
  )[0]

  if (!glossaryLink) {
    return null
  }

  const link = glossaryLink.split('=')[2]
  const term = link.split('"')[0]

  return decodeURIComponent(term)
}

export const contentReplaceLazyLoadImage = (description: string) => {
  return description.replace(
    new RegExp('src="([^ ]+?)(\\s+[^"]+)*"', 'g'),
    'src="$1" loading="lazy"'
  )
}

export const contentReplaceVideo = (description: string) => {
  return description.replace(REGEX_LINKS, (match, p1) => {
    const href = p1.match(/(?:\s|^)href=(["'])(.*?)\1(?:\s|$)/iu)
    if (!href) {
      return match
    }

    const videoUrls = href[2].match(REGEX_VIDEO_HREF)

    if (!videoUrls) {
      return match
    }

    const videoUrl = videoUrls[0]

    if (videoUrl.includes('youtube') && !videoUrl.includes('watch')) {
      return match
    }

    return `<div> <video-container url="${videoUrl.replace(
      /^http:\/\//i,
      'https://'
    )}" /> </div>`
  })
}

export const contentRemoveCurly = (description: string) => {
  return description.replace(/{{([^}]+)}}/g, '')
}

export const contentStripHTMLTags = (content: string) => {
  return content.replace(/<[^>]*>/g, '')
}

export const contentRemoveIf = (content: string) => {
  return content.replace(/v-if="(.*?)"/g, '')
}

export const contentRemoveStyleBlock = (content: string) => {
  return contentCleanTags(content, ['style'])
}

export const contentRemoveHtmlComment = (content: string) => {
  return content.replace('/---html', '').replace('\\---', '')
}

export const contentRemoveByDiv = (content: string, className: string) => {
  return content.replace(
    new RegExp(`<div class=.${className}.*?</div>`, 'g'),
    ''
  )
}

export const contentReplaceLink = (description: string) => {
  return description.replace(REGEX_LINKS, (match, p1, p2) => {
    const attributes = parseAttributes(p1)

    if (attributes.href && attributes.href.startsWith('http')) {
      return match
    }

    if (match.includes('javascript:')) {
      return match
    }

    const target = attributes.target ?? '_self'
    const className = attributes.class || ''

    if (attributes.href && attributes.href.startsWith('#')) {
      return `<a href="${attributes.href}" target="${target}"${
        className ? ` class="${className}"` : ''
      }>${p2}</a>`
    }

    return `<basic-router-link to="${attributes.href}" target="${target}"${
      className ? ` class="${className}"` : ''
    }>${p2}</basic-router-link>`
  })
}

export const contentCleanTags = (
  content: string,
  tags: string[],
  clean: boolean = true
): string => {
  const tagList = tags.join('|').replace(/[^a-z0-9-|_]+/gi, '')
  const reContent = new RegExp(
    '<('.concat(tagList).concat(')(?:\\s[^>]+)?>(.*?)<\\/\\1>'),
    'gi'
  )
  const replacedContent = content.replace(reContent, clean ? '' : '$2')
  const replacedTags = tags.reduce((acc, tag) => {
    const expression = new RegExp(`(<${tag}[\\w\\W]+${tag}>)`, 'g')
    return acc.replace(expression, '')
  }, replacedContent)

  return replacedTags
}

export const contentPreserveTags = (
  content: string,
  tags: string[],
  clean: boolean = true
): string => {
  const tagList = tags.join('|').replace(/[^a-z0-9-|_]+/gi, '')
  const reContent = new RegExp(
    '<(?!'.concat(tagList).concat(')([a-z0-9-_]+)(?:\\s[^>]+)?>(.*?)<\\/\\1>'),
    'gi'
  )
  const reTags = new RegExp(
    '<(?:\\/)?(?!'
      .concat(tagList)
      .concat(')([a-z0-9-_]+)(?:\\s[^>]+)?(?:\\s*\\/)?>'),
    'gi'
  )
  return content.replace(reContent, clean ? '' : '$2').replace(reTags, '')
}

export const fixCommonProblems = (description: string) => {
  return description.replace(/WIDTH/g, 'width')
}

export const getStyles = (description: string) => {
  const styles = description.match(/(<style[\w\W]+style>)/g)

  const matchedStyles = styles?.[0] ?? ''
  const rawStyles = matchedStyles.replace(/(<([^>]+)>)/gi, '')
  return rawStyles
}
